import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { Loader2Icon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/utils";

const buttonVariants = cva(
  "inline-flex items-center tracking-normal justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-button-primary hover:bg-button-primary-hover active:bg-button-primary-active text-primary-foreground shadow border border-primary",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-button-outline hover:bg-button-outline-hover active:bg-button-outline-active shadow shadow-gray-100",
        secondary:
          "bg-secondary text-secondary-foreground hover:filter hover:brightness-95",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // Toolbar dropdown triggers (filters, sort): bordered with no lift, filled
        // while hovered or open.
        flat: "border border-border bg-background font-normal hover:bg-accent data-[state=open]:bg-accent",
        link: "underline-offset-4 hover:underline !p-0 !h-auto text-blue-500 hover:text-blue-600",
      },
      size: {
        default: "h-8 rounded-md px-4 py-2",
        xs: "h-7 rounded-sm px-3 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-9 rounded-md px-8",
        icon: "h-8 w-8 p-1.5",
        xsIcon: "h-7 w-7 rounded-sm p-1.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  // Disables the button and shows a spinner before its label while an action runs.
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // If className contains a solid bg-* (not gradient), add bg-none to remove any gradient
    const hasSolidBg =
      className && /\bbg-(?!gradient|none|auto)[\w-]+/.test(className);

    const bgImage = hasSolidBg ? "bg-none" : "";

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className, bgImage)}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <Loader2Icon className="mr-2 size-4 animate-spin motion-reduce:animate-none" />
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
